import { useState } from 'react'
import styles from '../../styles/emailTemplateCard.module.css'
import { cn } from '@/lib/utils/helpers'
import type { EmailTemplateAddEditFormProps } from '../../types/components'
import { EmailTemplateFieldError } from './EmailTemplateFieldError'
import { EmailTemplateFormSection } from './EmailTemplateFormSection'
import { EmailTemplateVariablesList } from './EmailTemplateVariablesList'

export function EmailTemplateRecipientsSection({ page }: EmailTemplateAddEditFormProps) {
  const { recipientVars, register, registerField, errors, showFieldError, fieldErrorMessage, templateDetail } = page
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const notificationUsers = templateDetail?.notification_users ?? []

  return (
    <EmailTemplateFormSection testId="et-section-recipients" title="Email Recipients">
      <p className={cn(styles.privacyNote, 'mt-0 mb-3 text-sm text-slate-700')}>
        This section outlines all email recipients that will receive this email template. The To field
        must contain at least one valid email address, distribution list, or email variable to be
        considered a valid template.
        <br />
        Multiple email addresses can be listed in these fields. Each email address must be separated by
        an , symbol.
        <br />
        <strong>For example: email1@gmail.com, email2@gmail.com</strong>
      </p>

      <EmailTemplateVariablesList
        variables={recipientVars}
        testId="et-recipient-variables"
      />

      <div className={styles.formRow}>
        <div className={cn(styles.formCol, styles.formColHalf, 'min-w-0')}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>From</label>
            <input
              className={cn(styles.customFormControl, 'min-h-[2.375rem]')}
              placeholder="From"
              {...register('email_from')}
            />
          </div>
        </div>
        <div className={cn(styles.formCol, styles.formColHalf, 'min-w-0')}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              To <span className={styles.requiredStar}>*</span>
              {notificationUsers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowNotificationModal(true)}
                  className="ml-2 text-xs font-normal text-[rgb(21_151_255)] hover:underline cursor-pointer"
                >
                  Emails from Notification
                </button>
              )}
            </label>
            <input
              className={cn(
                styles.customFormControl,
                'min-h-[2.375rem]',
                errors.email_to && '!border-red-500',
              )}
              placeholder="To *"
              {...registerField('email_to')}
            />
            <EmailTemplateFieldError
              show={showFieldError('email_to')}
              message={fieldErrorMessage('email_to')}
            />
          </div>
        </div>

      {showNotificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Additional TO Address</h3>
            <p className="text-sm text-slate-600 mb-4">
              Below users will also get this email from notification settings, to change setting click on user name:
            </p>
            <div className="flex flex-col gap-2 max-h-60 overflow-y-auto mb-6">
              {notificationUsers.map((u) => (
                <a
                  key={u.user_id}
                  href={`/user-management/edit/${u.user_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-[rgb(21_151_255)] hover:underline"
                >
                  {u.name}
                </a>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowNotificationModal(false)}
                className="rounded-lg bg-[rgb(21_151_255)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95"
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}
        <div className={cn(styles.formCol, styles.formColHalf, 'min-w-0')}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>CC</label>
            <input
              className={cn(styles.customFormControl, 'min-h-[2.375rem]')}
              {...register('email_cc')}
            />
          </div>
        </div>
        <div className={cn(styles.formCol, styles.formColHalf, 'min-w-0')}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>BCC</label>
            <input
              className={cn(styles.customFormControl, 'min-h-[2.375rem]')}
              {...register('email_bcc')}
            />
          </div>
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={cn(styles.formCol, styles.formColHalf, 'min-w-0')}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="et-reply-to">
              Reply to
            </label>
            <input
              id="et-reply-to"
              className={cn(styles.customFormControl, 'min-h-[2.375rem]')}
              placeholder="Reply to"
              {...register('reply_to')}
            />
          </div>
        </div>
      </div>
    </EmailTemplateFormSection>
  )
}
